/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, Input, OnInit} from '@angular/core';
import {CommonModule, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgEventBus} from 'ng-event-bus';
import {MapToolsService} from '../services/map-tools.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {RemovalTypeLookup} from '../../common/models/removal-type.model';
import {SpatialSnapshotService} from '../snapshot/spatial-snapshot.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {InterestService} from '../services/features/interest.service';
import {MapService} from '../services/map.service';
import {EnlargementService} from '../widgets/enlargement/services/enlargement.service';
import {of, tap} from 'rxjs';
import {AnnotationService} from '../services/features/annotation.service';
import {PolygonService} from '../services/features/polygon.service';
import {FeatureAssociationService} from '../services/features/feature-association.service';
import {switchMap} from 'rxjs/operators';
import {LineService} from '../services/features/line.service';
import {LookupService} from '../../common/service/lookup.service';
import {TraitsService} from '../../common/service/traits.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FeaturesDeletionService} from '../services/features-deletion.service';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';
import {NgSelectModule} from '@ng-select/ng-select';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';

@Component({
    selector: 'nlr-removals',
    imports: [FormsModule, NgSelectModule, NlrPermissionsDisabledPipe],
    providers: [CommonModule],
    templateUrl: './removals.component.html',
    styleUrls: ['./removals.component.scss']
})
export class RemovalsComponent implements OnInit {
    @Input()
    disabled: boolean;

    title = 'RemovalsComponent';

    permissions = ['EDIT_DESIGN', 'VIEW_DESIGN'];

    removalTypes: RemovalTypeLookup[] = [];

    selectedRemovalType = {};

    deleteAllSelectedFeaturesTimer: number;

    deletionTimer: number;

    constructor(
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private annotationService: AnnotationService,
        private enlargementService: EnlargementService,
        private featureAssociationService: FeatureAssociationService,
        private featuresDeletionService: FeaturesDeletionService,
        private interestService: InterestService,
        private lineService: LineService,
        private lookupService: LookupService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private permissionsService: PermissionsService,
        private polygonService: PolygonService,
        private spatialUtilService: SpatialUtilService,
        private spatialSnapshotService: SpatialSnapshotService,
        private traitsService: TraitsService
    ) {}

    ngOnInit() {
        if (this.removalTypes.length === 0) {
            this.getRemovalTypeLookups();
        }

        this.eventBus
            .on('nlr:trait-service:traitsChanged')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.getRemovalTypeLookups());

        this.mapToolsService.init(
            'DELETE_SELECTED_FEATURES',
            () => {
                if (this.deleteAllSelectedFeaturesTimer) {
                    window.clearTimeout(this.deleteAllSelectedFeaturesTimer);
                }

                this.deleteAllSelectedFeaturesTimer = window.setTimeout(() => this.featuresDeletionService.deleteFeatures().subscribe(), 100);
            },
            null,
            () => this.isDisabled()
        );
    }

    showComponent() {
        return this.permissionsService.hasPermission(this.permissions);
    }

    isDisabled() {
        return this.disabled || !this.permissionsService.hasEditPermission(this.permissions);
    }

    getTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('DELETE_SELECTED_FEATURES', 'Delete selected features');
    }

    deleteAllSelectedFeatures() {
        console.debug(`${this.title} - deleteAllSelectedFeatures`);

        this.mapToolsService.activate('DELETE_SELECTED_FEATURES').subscribe();
    }

    executeRemovalType(removalType: RemovalTypeLookup) {
        console.debug(`${this.title} - executeRemovalType`);

        this.mapToolsService
            .activate(
                'DELETE_OTHER_FEATURES',
                () => {
                    switch (removalType.id) {
                        case 1: {
                            // Deletes all points, lines, polygons, annotation, lots, roads and enlargements
                            // and refreshes the interests from the plan
                            const subscription = this.spatialSnapshotService
                                .createSnapshot({showAlert: true})
                                .pipe(switchMap(() => this.deleteAllFeatures()))
                                .subscribe();
                            this.eventBus.cast('nlr:busy', subscription);
                            break;
                        }
                        case 2: {
                            // Deletes all enlargements and enlargement annotation from the plan
                            const subscription = this.spatialSnapshotService
                                .createSnapshot({showAlert: true})
                                .pipe(switchMap(() => this.deleteAllEnlargements()))
                                .subscribe();
                            this.eventBus.cast('nlr:busy', subscription);
                            break;
                        }
                        case 3:
                            // Deletes all lots and lot annotation from the plan
                            this.deleteAllLots();
                            break;
                        case 4:
                            // Removes all lot / lot polygon links and deletes all lot annotation from the plan (bidirectional).
                            // Removes all instances of lot annotation from the main plan and enlargements
                            this.unlinkAllLotsFromPolygons();
                            break;
                        case 5:
                            // Removes all interest / polygon and interest / freehand line links and deletes all
                            // interest subject annotation from the plan
                            // Removes all instances of lot annotation from the main plan. Removes all enlargements
                            this.unlinkAllInterests();
                            break;
                        case 6:
                            // Removes all road / road polygon and road / road widening polygon links and
                            // deletes all road annotation from the plan. Removes all enlargements.
                            this.unlinkAllRoadsFromPolygons();
                            break;
                    }
                },
                null,
                () => this.isDisabled()
            )
            .subscribe();
    }

    private deleteAllFeatures() {
        console.debug(`${this.title} - deleteAllFeatures`);

        //ND-12 => ND-972 moved vm.unlinkAllRoads() to the top of this stack as features are required to be unlinked before deleting
        this.unlinkAllRoads();

        return this.deleteAllEnlargements().pipe(
            switchMap(() => this.clearInterestAssociations()),
            switchMap(() =>
                this.spatialUtilService.clearFeatureClasses([
                    'angle',
                    'line',
                    'point',
                    'controlpoint',
                    'constructpoint',
                    'constructline',
                    'lot',
                    'polygon',
                    'annotation',
                    'polyline',
                    'printarea'
                ])
            ),
            tap(() => {
                this.interestService.deleteAllInterestGeometries();
                this.spatialUtilService.unhighlightAllFeatures();
                this.mapService.refMapScale = 750;
            }),
            switchMap(() => this.spatialUtilService.save())
        );
    }

    private clearInterestAssociations() {
        const allInterests = this.spatialUtilService.getInterests();
        if (!allInterests.length) {
            return of(null);
        }

        allInterests.forEach((interest: any) => {
            interest.attributes.polygonids = null;
            interest.attributes.polylineids = null;
        });

        return this.spatialUtilService
            .applyEdits('interest', null, allInterests, null)
            .pipe(tap((res: any) => console.debug('clearInterestAssociations - interestFeatureLayer.applyEdits success:', res)));
    }

    private deleteAllEnlargements() {
        console.debug(`${this.title} - deleteAllEnlargements`);

        return this.spatialUtilService.clearFeatureClass('clipping').pipe(
            switchMap(() => {
                this.spatialUtilService.getEnlargements().forEach((e: any) => this.enlargementService.delete(undefined, e, undefined));

                // Delete the lingering enlargement annotation from the main plan. This will still be present if
                // an enlargement was selected when 'deleteAllEnlargements' was invoked
                const lingeringEnlargementAnnotations = this.spatialUtilService
                    .getAnnotations()
                    .filter((a: any) => a.attributes.featuretype === 'enlargement');

                if (lingeringEnlargementAnnotations.length) {
                    return this.annotationService.deleteAnnotations(lingeringEnlargementAnnotations);
                } else {
                    return of([]);
                }
            })
        );
    }

    private deleteAllLots() {
        console.debug(`${this.title} - deleteAllLots`);

        this.spatialSnapshotService.createSnapshot({showAlert: true}).subscribe(() => {
            const layersToDeleteInOrder = [this.spatialUtilService.getLotMapLayer()];

            if (this.deletionTimer) {
                window.clearTimeout(this.deletionTimer);
            }

            this.deletionTimer = window.setTimeout(() => {
                this.deleteAllFeaturesFromLayers(layersToDeleteInOrder);
                this.removeAllPolygonToLotAssociations();
                this.interestService.deleteAllInterestGeometries();
            }, 1000);
        });
    }

    private deleteAllFeaturesFromLayers(layersToDeleteInOrder: any[]) {
        console.debug(`${this.title} - deleteAllFeaturesFromLayers`);

        const featuresToDelete = layersToDeleteInOrder.reduce((acc, v) => {
            acc.push(...this.spatialUtilService.getFeatures(v));
            return acc;
        }, []);

        if (this.deletionTimer) {
            window.clearTimeout(this.deletionTimer);
        }

        this.deletionTimer = window.setTimeout(() => {
            layersToDeleteInOrder.forEach(layer => {
                if (featuresToDelete.length > 0) {
                    this.featuresDeletionService.deleteFeatures(featuresToDelete, layer, {createSnapshot: false}).subscribe();
                }
            });
        }, 1200);
    }

    private unlinkAllLotsFromPolygons() {
        console.debug(`${this.title} - unlinkAllLotsFromPolygons`);

        this.spatialSnapshotService.createSnapshot({showAlert: true}).subscribe(() => {
            const allLots = this.spatialUtilService.getLots();
            if (this.deletionTimer) {
                window.clearTimeout(this.deletionTimer);
            }

            this.deletionTimer = window.setTimeout(() => {
                this.removeAllPolygonToLotAssociations();
                this.removeAllLotToPolygonAssociations(allLots);
                this.removeLingeringLotAnnotations();
                // DCX-8409: remove blue highlight borders for interests
                this.removeAllLotToInterests(allLots);
            }, 1000);
        });
    }

    private removeAllPolygonToLotAssociations() {
        console.debug(`${this.title} - removeAllPolygonToLotAssociations`);

        this.spatialUtilService.getPolygons().forEach((polygon: any) => this.polygonService.removeBusinessFeatureIdsFromPolygon(polygon, 'lot'));
    }

    private removeAllLotToPolygonAssociations(allLots: any[]) {
        console.debug(`${this.title} - removeAllLotToPolygonAssociations`);

        const allPolygons = this.spatialUtilService.getPolygons();
        allLots.forEach(lot =>
            this.featureAssociationService
                .updateBusinessFeatureAssociationChanges(
                    this.spatialUtilService.getLotMapLayer(),
                    this.spatialUtilService.copyGraphic(lot),
                    allPolygons,
                    [],
                    'polygon'
                )
                .subscribe()
        );
    }

    private removeLingeringLotAnnotations() {
        console.debug(`${this.title} - removeLingeringLotAnnotations`);

        // Deletes lingering lot annotations from the main plan. These will still be present
        // when 'removalAllLotToPolygon/PolygonToLotAssociations' are invoked
        const lingeringLotAnnotations = this.spatialUtilService.getAnnotations().filter((a: any) => a.attributes.featuretype === 'lot');
        if (lingeringLotAnnotations.length) {
            this.annotationService.deleteAnnotations(lingeringLotAnnotations).subscribe();
        }
    }

    /***
     * Clear any interests that are associated with the lots with empty geometries in order to clear the blue border
     * DCX-8409
     * @param allLots features
     */
    private removeAllLotToInterests(allLots: any[]) {
        console.debug(`${this.title} - removeAllLotToInterests`);

        const lotNames = allLots.map(l => l && l.attributes.name).filter(v => v);

        if (lotNames.length) {
            this.interestService.deleteLotInterestGeometries(lotNames);
        }
    }

    private unlinkAllInterests() {
        console.debug(`${this.title} - unlinkAllInterests`);

        const subscription = this.spatialSnapshotService
            .createSnapshot({showAlert: true})
            .pipe(
                switchMap(() => this.removeAllInterestToPolygonAssociations(this.spatialUtilService.getInterests())),
                tap(() => {
                    this.removeAllPolygonToInterestAssociations();
                    this.removeAllInterestPolylineAssociations(this.spatialUtilService.getInterests());
                    this.removeLingeringInterestAnnotations();
                })
            )
            .subscribe();

        this.eventBus.cast('nlr:busy', subscription);
    }

    private removeAllInterestToPolygonAssociations(allInterests: any[]) {
        console.debug(`${this.title} - removeAllInterestToPolygonAssociations`);

        const allPolygons = this.spatialUtilService.getPolygons();
        return forkJoinDefaultIfEmpty(
            allInterests.map(interest =>
                this.featureAssociationService.updateBusinessFeatureAssociationChanges(
                    this.spatialUtilService.getInterestMapLayer(),
                    this.spatialUtilService.copyGraphic(interest),
                    allPolygons,
                    [],
                    'polygon'
                )
            )
        );
    }

    private removeAllInterestPolylineAssociations(allInterests: any[]) {
        console.debug(`${this.title} - removeAllInterestToPolylineAssociations`);

        const allPolylines = this.spatialUtilService.getPolylines();

        if (allPolylines.length === 0) {
            return;
        }

        allInterests.map(interest =>
            this.featureAssociationService
                .updateBusinessFeatureAssociationChanges(
                    this.spatialUtilService.getInterestMapLayer(),
                    this.spatialUtilService.copyGraphic(interest),
                    allPolylines,
                    [],
                    'polyline'
                )
                .subscribe()
        );

        allPolylines.forEach((polyline: any) => (polyline.attributes.interestids = null));

        this.lineService.applyPolylineUpdates(allPolylines);
    }

    private removeAllPolygonToInterestAssociations() {
        console.debug(`${this.title} - removeAllPolygonToInterestAssociations`);

        this.spatialUtilService.getPolygons().forEach((polygon: any) => this.polygonService.removeBusinessFeatureIdsFromPolygon(polygon, 'interest'));
    }

    private removeLingeringInterestAnnotations() {
        console.debug(`${this.title} - removeLingeringInterestAnnotations`);

        // Deletes lingering interest annotations from the main plan. These will still be present
        // when 'removalAllInterestToPolygon/PolygonToInterestAssociations' are invoked
        const lingeringInterestAnnotations = this.spatialUtilService.getAnnotations().filter((a: any) => a.attributes.featuretype === 'interest');
        if (lingeringInterestAnnotations.length) {
            this.annotationService.deleteAnnotations(lingeringInterestAnnotations).subscribe();
        }
    }

    private unlinkAllRoadsFromPolygons() {
        console.debug(`${this.title} - unlinkAllRoadsFromPolygons`);

        this.spatialSnapshotService.createSnapshot({showAlert: true}).subscribe(() => {
            if (this.deletionTimer) {
                window.clearTimeout(this.deletionTimer);
            }
            this.deletionTimer = window.setTimeout(() => this.unlinkAllRoads(), 1000);
        });
    }

    private unlinkAllRoads() {
        const roads = this.spatialUtilService.getRoads();
        this.removeAllRoadToPolygonAssociations(roads);
        this.removeAllPolygonToRoadAssociations();
        this.removeLingeringRoadAnnotations();
    }

    private removeAllRoadToPolygonAssociations(allRoads: any[]) {
        console.debug(`${this.title} - removeAllRoadToPolygonAssociations`);

        const allPolygons = this.spatialUtilService.getPolygons();
        allRoads.forEach(road =>
            this.featureAssociationService
                .updateBusinessFeatureAssociationChanges(
                    this.spatialUtilService.getRoadMapLayer(),
                    this.spatialUtilService.copyGraphic(road),
                    allPolygons,
                    [],
                    'polygon'
                )
                .subscribe()
        );
    }

    private removeAllPolygonToRoadAssociations() {
        console.debug(`${this.title} - removeAllPolygonToRoadAssociations`);

        this.spatialUtilService.getPolygons().forEach((polygon: any[]) => this.polygonService.removeBusinessFeatureIdsFromPolygon(polygon, 'road'));
    }

    private removeLingeringRoadAnnotations() {
        console.debug(`${this.title} - removeLingeringRoadAnnotations`);

        // Deletes lingering road annotations from the main plan. These will still be present
        // when 'removeAllRoadToPolygon/PolygonToRoadAssociations' are invoked
        const lingeringRoadAnnotations = this.spatialUtilService.getAnnotations().filter((a: any) => a.attributes.featuretype === 'road');
        if (lingeringRoadAnnotations.length) {
            this.annotationService.deleteAnnotations(lingeringRoadAnnotations).subscribe();
        }
    }

    private getRemovalTypeLookups() {
        console.debug(`${this.title} - getRemovalTypeLookups`);

        const filteredTypes = this.lookupService
            .getLookups('removalType')
            .filter((r: RemovalTypeLookup) => r.active && this.traitsService.criteriaMatches(r.planTraitCriteria, true));

        this.removalTypes = [...filteredTypes];
    }
}
